import type { Root } from './prototypeTypes';

/**
 * Replaces the non-essential mobile brand chrome with a contextual back row
 * only after a Topic/Chapter poster has genuinely left the viewport. The
 * observer is route-safe: the compact row is removed immediately when the
 * active view changes or the viewport grows back to desktop width.
 */
export function mountMobileContextHeader(root: Root): { destroy(): void } {
	const doc = root.ownerDocument ?? document;
	const nav = root.querySelector<HTMLElement>('.top-nav');
	if (!nav) return { destroy() {} };
	const compact = doc.createElement('div');
	compact.id = 'mobileContextHeader';
	compact.className = 'mobile-context-header';
	compact.hidden = true;
	nav.appendChild(compact);
	const query = doc.defaultView?.matchMedia?.('(max-width: 640px)');
	let activeHero: HTMLElement | null = null;
	let activeContext = '';

	const readContext = () => {
		const article = root.querySelector<HTMLElement>('#view-article.active');
		const topic = root.querySelector<HTMLElement>('#view-topic.active');
		const title = article?.querySelector<HTMLElement>('#article-title')?.textContent?.trim()
			?? topic?.querySelector<HTMLElement>('#topic-title')?.textContent?.trim()
			?? '';
		return { title, isArticle: Boolean(article) };
	};
	const render = ({ title, isArticle }: ReturnType<typeof readContext>) => {
		compact.innerHTML = `<button type="button" class="mobile-context-back" data-proto-click="${isArticle ? 'backToActiveTopic()' : 'backFromTopic()'}" aria-label="Back"><svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"></path></svg></button><span>${escapeHtml(title)}</span>`;
	};
	const setHidden = (hidden: boolean) => {
		if (compact.hidden !== hidden) compact.hidden = hidden;
	};
	const setContextOpen = (open: boolean) => {
		if (nav.classList.contains('mobile-context-open') !== open) nav.classList.toggle('mobile-context-open', open);
	};
	const sync = () => {
		if (!query?.matches) {
			setHidden(true);
			setContextOpen(false);
			activeHero = null;
			activeContext = '';
			return;
		}
		const hero = root.querySelector<HTMLElement>('#view-article.active #chapter-hero, #view-topic.active #topic-cover');
		const context = readContext();
		const contextKey = `${context.isArticle ? 'chapter' : 'topic'}:${context.title}`;
		if (hero !== activeHero || contextKey !== activeContext) {
			activeHero = hero;
			activeContext = contextKey;
			render(context);
		}
		if (!hero) {
			setHidden(true);
			setContextOpen(false);
			return;
		}
		const navBottom = nav.getBoundingClientRect().bottom;
		const show = hero.getBoundingClientRect().bottom <= navBottom;
		setHidden(!show);
		setContextOpen(show);
	};
	const onScroll = () => sync();
	const observer = new MutationObserver(sync);
	doc.defaultView?.addEventListener('scroll', onScroll, { passive: true });
	doc.defaultView?.addEventListener('resize', sync);
	query?.addEventListener?.('change', sync);
	observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
	sync();
	return {
		destroy() {
			doc.defaultView?.removeEventListener('scroll', onScroll);
			doc.defaultView?.removeEventListener('resize', sync);
			query?.removeEventListener?.('change', sync);
			observer.disconnect();
			compact.remove();
			setContextOpen(false);
		},
	};
}

function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character] ?? character));
}
